import { useCallback, useRef } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { getRecaptchaSiteKey } from '../utils/config';

/**
 * Token sent when no site key (runtime config.js or build-time env).
 * Backend skips Google verification when its secret is also unset.
 */
export const LOCAL_DEV_RECAPTCHA_TOKEN = 'local-dev-recaptcha-token';

const READY_POLL_MS = 150;
const READY_TIMEOUT_MS = 8000;

/**
 * Use instead of useGoogleReCaptcha so local dev works without a site key.
 * Site key: runtime `config.js` (Docker) or `REACT_APP_RECAPTCHA_SITE_KEY` at build time.
 *
 * When a site key is configured, Google's script can still be undefined for a short time
 * after page load. This hook always returns a function and waits for the script instead of
 * failing the form with "still loading".
 */
export function usePolarisRecaptcha() {
  const { executeRecaptcha: googleExecute } = useGoogleReCaptcha();
  const hasSiteKey = Boolean(getRecaptchaSiteKey());
  const googleExecuteRef = useRef(googleExecute);
  googleExecuteRef.current = googleExecute;

  const executeRecaptcha = useCallback(
    async (action?: string) => {
      if (!hasSiteKey) {
        return LOCAL_DEV_RECAPTCHA_TOKEN;
      }

      let waited = 0;
      while (!googleExecuteRef.current) {
        if (waited >= READY_TIMEOUT_MS) {
          throw new Error('recaptcha-not-ready');
        }
        await new Promise((resolve) => setTimeout(resolve, READY_POLL_MS));
        waited += READY_POLL_MS;
      }
      return googleExecuteRef.current(action);
    },
    [hasSiteKey]
  );

  return {
    executeRecaptcha,
    recaptchaReady: !hasSiteKey || Boolean(googleExecute),
  };
}
